import React, { useState } from "react"
import { CsvProduct } from "~/models/Product"
import { format } from "~/utils/ExtendFunctions"
import { PlanType } from "~/models/Plan"
import { ID } from "~/models/Identification"
import { CheckBox } from "~/components/atoms/CheckBox"
import { ButtonParts } from "~/components/atoms/ButtonParts"
import { Modal } from "react-bootstrap"
import { Calculation } from "~/utils/Calculation"
import { SimpleHelpButton } from "~/components/molecules/SimpleHelpButton"
import { Api } from "~/api/Api";
import { ITEM_CATALOG_ID } from "~/data/constants/ItemCatalogId";
import { DISCOUNT_RATE } from "~/data/constants/DiscountRate"
import HtmlReactParser from "html-react-parser"

interface Props {
  name: string
  dataSource: CsvProduct[]
  planType?: `${PlanType}`
  view?: number
  selectIds?: ID[]
  onChange?: (productId: ID, checked: boolean, dataSource: CsvProduct) => void
  industryIds: ID[]
}

const getTarget = (selected: number) => {
  switch (selected) {
    case 2:
      return "多くの鈑金事業者"
    case 3:
      return "サービスステーション"
    case 4:
      return "車両販売事業者"
    default:
      return "多くの整備事業者"
  }
}

const getColor = (section2: number) => {
  switch (section2) {
    case 2:
      return "yellow"
    case 3:
      return "blue"
    case 4:
      return "green"
    default:
      return "green"
  }
}

export function SimplePackDetail(props: Props) {
  const { name, dataSource, planType, view = 0, onChange, selectIds = [], industryIds } = props
  const [isShown, setIsShown] = useState<boolean>(false)
  const [element, setElement] = useState<string>("")
  const [modalSize, setModalSize] = useState<"sm" | "lg" | "xl">("lg")

  /**
   * 従量課金詳細モーダルの表示/非表示を切り替えます。
   * @param csvProduct CSV商品データ
   */
  const togglePayAsYouGoUsageModal = async (csvProduct?: CsvProduct) => {
    if (!isShown) {
      setModalSize(csvProduct ? (csvProduct.modalType === 2 ? "xl" : "lg") : "lg")
      const content = await Api.getHtmlData(csvProduct.htmlKey, "pay-as-you-go")
      const dom = new DOMParser()
      const html = dom.parseFromString(content, "text/html").getElementsByClassName("modal-content")[0].innerHTML
      setElement(html)
    }
    setIsShown(!isShown)
  }

  const style = (() => {
    if (dataSource.slice(0, 3).every(data => {
      if (industryIds.includes(String(data.selected))) {
        return false
      }

      return ![
        ITEM_CATALOG_ID.ED_BASE_PACK,
        ITEM_CATALOG_ID.PP_BASE_PACK,
        ITEM_CATALOG_ID.MP_BASE_PACK,
        ITEM_CATALOG_ID.BLPAY_CUSTOMER_PACK,
        ITEM_CATALOG_ID.BLPAY_AFFILIATION_PACK,
      ].some(id => id === data.id)
    })) {
      return {
        marginTop: "0"
      }
    }
    return
  })()


  return (
    <>
      <div className="pack-panel-area" style={style}>
        {dataSource.map((data, index) => (
          <React.Fragment key={`${name}-${index}`}>
            {(index < view || !view) && (
              <div className="pack-panel">
                {industryIds.includes(String(data.selected)) && (
                  <div className="recommendation-balloon">
                    <div className="recommendation-text">{getTarget(data.selected)}へのおすすめ！</div>
                    <div className="balloon-arrow"></div>
                  </div>
                )}
                {data.id === ITEM_CATALOG_ID.ED_BASE_PACK && (
                  <div className="recommendation-balloon">
                    <div className="recommendation-text">この商品で電帳法改正対応できます！</div>
                    <div className="balloon-arrow"></div>
                  </div>
                )}
                {data.id === ITEM_CATALOG_ID.PP_BASE_PACK && (
                  <div className="recommendation-balloon">
                    <div className="recommendation-text">車両販売事業者様向け！</div>
                    <div className="balloon-arrow"></div>
                  </div>
                )}
                {data.id === ITEM_CATALOG_ID.MP_BASE_PACK && (
                  <div className="recommendation-balloon">
                    <div className="recommendation-text">自動車補修事業者様向け！</div>
                    <div className="balloon-arrow"></div>
                  </div>
                )}
                {data.id === ITEM_CATALOG_ID.BLPAY_CUSTOMER_PACK && (
                  <div className="recommendation-balloon">
                    <div className="recommendation-text">購入事業者向け！</div>
                    <div className="balloon-arrow"></div>
                  </div>
                )}
                {data.id === ITEM_CATALOG_ID.BLPAY_AFFILIATION_PACK && (
                  <div className="recommendation-balloon">
                    <div className="recommendation-text">販売事業者向け！</div>
                    <div className="balloon-arrow"></div>
                  </div>
                )}

                <div className={`pack-panel-${getColor(data.section2)}`}></div>
                <div className="pack-panel-header">
                  <div className="checkbox-area">
                    <div className="form-group">
                      <div className="form-check custom-control custom-checkbox">
                        <CheckBox
                          name={`${name}-${data.id}-${index}`}
                          value={{ label: data.name, checked: selectIds.includes(data.id) }}
                          onChange={(checked) => {
                            onChange?.apply(this, [data.id, checked, data])
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <SimpleHelpButton data={data} name={data.htmlKey} />
                </div>
                <div className="pack-panel-body">
                  <div className="point-text">{data.point}</div>
                  <div className="pack-text">{data.description || data.explain}</div>
                  <div className="pack-fee-info">
                    <div className="fee-title">
                      <div className="title">料金</div>
                    </div>
                    <div className="fee-area">
                      <div className="fee">
                        {data.itemKind !== "0" ?
                          <React.Fragment>
                            {data.name === ".c mobileサービス" ?
                              <div className="fee-month">
                                <span className="font-weight-bold">
                                  アカウント(ID)の料金に含む
                                </span>
                              </div>
                              :
                              <React.Fragment>
                                {data.category !== "sptFnc" ?
                                  <div className="fee-year">
                                    {planType === PlanType.Month
                                      ? DISCOUNT_RATE.find(rate => rate.id === PlanType.Month)?.title
                                      : ((data?.price?.amounts?.[planType] || 0) > 0
                                          ? DISCOUNT_RATE.find(rate => rate.id === planType)?.title
                                          : DISCOUNT_RATE.find(rate => rate.id === PlanType.Month)?.title
                                      )
                                    }
                                  </div>
                                  : ""
                                }
                                <div className="fee-month">
                                  <span className="money">{format(Calculation.price(data, planType))}</span>
                                  {data.category !== "sptFnc" ?
                                    planType === PlanType.Month
                                      ? <span className="month">/月</span>
                                      : ((data?.price?.amounts?.[planType] || 0) > 0 ? "" :
                                        <span className="month">/月</span>)
                                    : ""
                                  }
                                </div>
                              </React.Fragment>
                            }
                          </React.Fragment>
                          :
                          <div className="fee-month">
                            <span className="money pay-as-you-go">使った分だけお支払い</span><br />
                            <ButtonParts className={"text-link"}
                              action={togglePayAsYouGoUsageModal.bind(this, data)}>詳細はこちら</ButtonParts>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                  {data.id === ITEM_CATALOG_ID.PP_BASE_PACK && (
                    <div className="pack-note asterisk">月々のPDF出力枚数に応じて従量料金が発生します。<br />
                      100枚印刷ごとに500円かかります。</div>
                  )}
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      <Modal show={isShown} onHide={togglePayAsYouGoUsageModal} size={modalSize}>
        {HtmlReactParser(element)}
        <Modal.Footer>
          <ButtonParts className={"btn btn-success"} action={togglePayAsYouGoUsageModal}>閉じる</ButtonParts>
        </Modal.Footer>
      </Modal>
    </>
  )
}
